import {Inject, Injectable} from '@angular/core';
import {Params, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../state/app.state";
import {BehaviorSubject, catchError, map, Subject, throwError} from "rxjs";
import {HttpClient, HttpResponse} from "@angular/common/http";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {DatePipe, DOCUMENT} from "@angular/common";
import {AbstractControl, FormGroup, ValidationErrors} from "@angular/forms";
import {MatSnackBar} from "@angular/material/snack-bar";
import {
  ClearState as ClearAuthenticationState,
  LogoutRequest
} from "../../authentication/core/authentication.actions";
import {
  ClearSelectedUserData,
  ClearState as ClearSharedState,
  GenerateUsernameRequest,
  SetDrillDownUrls,
  VerifyUsernameRequest
} from "../../shared/core/shared.actions";
import {
  DialogBoxComponent
} from "../components/dialogbox/basic-dialog/dialog-box.component";
import {
  ConfirmationDialogComponent
} from "../components/dialogbox/confirmation-dialog/confirmation-dialog.component";
import {drillDownUrls} from "../core/shared.selectors";
import {
  ClearState as ClearAdminGroupsListState
} from "../../page-modules/admin-groups-list/core/admin-groups-list.actions";
import {
  ClearState as ClearBehaviorTrackerState
} from "../../page-modules/behavior-tracker/core/behavior-tracker.actions";
import {
  ClearState as ClearClientDetailsState
} from "../../page-modules/client-details/core/client-details.actions";
import {
  ClearState as ClearClientsListState
} from "../../page-modules/clients-list/core/clients-list.actions";
import {
  ClearState as ClearCoachGroupsListState
} from "../../page-modules/coach-groups-list/core/coach-groups-list.actions";
import {
  ClearState as ClearGroupDashboardState
} from "../../page-modules/group-dashboard/core/group-dashboard.actions";
import {
  ClearState as ClearGroupDetailsState
} from "../../page-modules/group-details/core/group-details.actions";
import {
  ClearState as ClearInviteState
} from "../../page-modules/invite/core/invite.actions";
import {
  ClearState as ClearManageUsersState
} from "../../page-modules/manage-users/core/manage-users.actions";
import {
  ClearState as ClearMetricsState
} from "../../page-modules/metrics/core/metrics.actions";
import {
  ClearState as ClearNotificationsState
} from "../../page-modules/notifications/core/notifications.actions";
import {
  ClearState as ClearParticipantsListState
} from "../../page-modules/participants-list/core/participants-list.actions";
import {
  ClearState as ClearQuestionnairesState
} from "../../page-modules/questionnaire/core/questionnaires.actions";
import {
  ClearState as ClearResourceHubState
} from "../../page-modules/resources-hub/core/resources-hub.actions";
import {
  ClearState as ClearSessionsListState
} from "../../page-modules/sessions-list/core/sessions-list.actions";
import {
  ClearState as ClearReportsState
} from "../../page-modules/reports/core/reports.actions";
import {
  ClearState as ClearProgressState
} from "../../page-modules/progress/core/progress.actions";
import {
  ClearState as ClearContractsState
} from "../../page-modules/contracts/core/contracts.actions";
import {MessageService} from 'primeng/api';
import {HoursAndMinutesPipe} from '../pipes/hours-and-minutes.pipe';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  openMobileDialog: Subject<any> = new Subject();
  closeMobileDialog: Subject<void> = new Subject();
  onNavigationEnd: BehaviorSubject<any> = new BehaviorSubject({});
  alphabetsRegex = `^[^-\\s]([a-zA-Z])+$`;
  alphanumericRegex = `^[^-\\s]([0-9a-zA-Z])+$`;
  requestedUserId: BehaviorSubject<number> = new BehaviorSubject(null);
  requestedGroupId: BehaviorSubject<number> = new BehaviorSubject(null);
  requestedResourceId: BehaviorSubject<any[]> = new BehaviorSubject([]);
  isLoadingScreenOpen = new Subject<boolean>();

  urlParams: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  sideNavCollapse: BehaviorSubject<any> = new BehaviorSubject<boolean>(null);

  // Used for copyToClipboard(value)
  private dom: Document;

  constructor(
    private datePipe: DatePipe,
    @Inject(DOCUMENT) dom?: Document,
    private store?: Store<fromRoot.State>,
    private router?: Router,
    private http?: HttpClient,
    private dialog?: MatDialog,
    private snackBar?: MatSnackBar,
    private messageService?: MessageService,
    private hoursAndMinutesPipe?: HoursAndMinutesPipe,
  ) {
    this.dom = dom;
  }

  setToLocalStorage(key: string, value: string) {
    localStorage.setItem(key, value);
  }

  removeFromLocalStorage(key: string) {
    localStorage.removeItem(key);
  }

  getFromLocalStorage(key: string) {
    return localStorage.getItem(key);
  }

  setToSessionStorage(key: string, value: string) {
    sessionStorage.setItem(key, value);
  }

  getFromSessionStorage(key: string) {
    return sessionStorage.getItem(key);
  }

  removeFromSessionStorage(key: string) {
    sessionStorage.removeItem(key);
  }

  setAuthenticationToken(token: string) {
    this.setToLocalStorage('ss-cord/token', token);
  }

  getAuthenticationToken() {
    return this.getFromLocalStorage('ss-cord/token');
  }

  removeAuthenticationToken() {
    return this.removeFromLocalStorage('ss-cord/token');
  }

  isAuthenticated() {
    return !!this.getAuthenticationToken();
  }

  setPasswordToken(token: string) {
    this.setToLocalStorage('ss-cord/set-password-token', token);
  }

  getPasswordToken() {
    return this.getFromLocalStorage('ss-cord/set-password-token');
  }

  isPasswordTokenPresent() {
    return !!this.getPasswordToken();
  }

  removeSetPasswordToken() {
    return this.removeFromLocalStorage('ss-cord/set-password-token');
  }

  setRegisterToken(token: string) {
    this.setToLocalStorage('ss-cord/register-token', token);
  }

  getRegisterToken() {
    return this.getFromLocalStorage('ss-cord/register-token');
  }

  isRegisterTokenPresent() {
    return !!this.getRegisterToken();
  }

  removeRegisterToken() {
    return this.removeFromLocalStorage('ss-cord/register-token');
  }

  setRegisterOtpToken(token: string) {
    this.setToLocalStorage('ss-cord/register-otp-token', token);
  }

  getRegisterOtpToken() {
    return this.getFromLocalStorage('ss-cord/register-otp-token');
  }

  isRegisterOtpTokenPresent() {
    return !!this.getRegisterOtpToken();
  }

  removeRegisterOtpToken() {
    return this.removeFromLocalStorage('ss-cord/register-otp-token');
  }

  setOnboardingToken(token: string) {
    this.setToLocalStorage('ss-cord/onboarding-token', token);
  }

  isOnboardingTokenPresent() {
    return !!this.getOnboardingToken();
  }

  getOnboardingToken() {
    return this.getFromLocalStorage('ss-cord/onboarding-token');
  }

  removeOnboardingToken() {
    return this.removeFromLocalStorage('ss-cord/onboarding-token');
  }

  set2FAT(token: string) {
    this.setToLocalStorage('ss-cord/2FA-token', token);
  }

  get2FAT() {
    return this.getFromLocalStorage('ss-cord/2FA-token');
  }

  remove2FAT() {
    return this.removeFromLocalStorage('ss-cord/2FA-token');
  }

  is2FATPresent() {
    return !!this.get2FAT();
  }

  setLoginOTPToken(token: string) {
    this.setToLocalStorage('ss-cord/login-otp-token', token);
  }

  getLoginOTPToken() {
    return this.getFromLocalStorage('ss-cord/login-otp-token');
  }

  removeLoginOTPToken() {
    return this.removeFromLocalStorage('ss-cord/login-otp-token');
  }

  isLoginOTPTokenPresent() {
    return !!this.getLoginOTPToken();
  }

  callAPI(type: string, url: string, payload?: any, options: any = {}) {
    if (url.includes('login') && !this.router.url.includes('login')) {
      return {}
    }

    // @ts-ignore
    let apiCall = this.http[type]<any>(url, payload, {
      observe: 'response',
      ...options,
    });

    if (type === 'delete') {
      apiCall = this.http[type]<any>(url, {
        observe: 'response',
        body: payload || {},
        ...options,
      });
    }

    return apiCall.pipe(
      map((response: any) => {
        if (response instanceof HttpResponse) {
          return response.body;
        }

        return response;
      }),
      catchError((error) => throwError(error))
    );
  }

  startLoading() {
    this.isLoadingScreenOpen.next(true);
  }

  stopLoading() {
    this.isLoadingScreenOpen.next(false);
  }

  openDesktopDialog(config: any = {}) {
    config = {
      ...config
    }
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = [
      'dialog',
      ...(config.panelWidth ? [`panel-width-${config.panelWidth}`] : [])
    ];
    return this.dialog.open(DialogBoxComponent, {...dialogConfig, ...config, autoFocus: false, ...(config.data.disableClose ? {disableClose: true} : {disableClose: false})})
  }

  closeDesktopDialog() {
    this.dialog.closeAll();
  }

  checkFormValidation(form: FormGroup, errorMessageMap: any, currentField?: string) {
    const errorMessages = {};
    const formControls = form.controls || null;

    if (currentField) {
      const errors = form.controls[currentField].errors;
      const firstErrorType = errors ? Object.keys(errors)[0] : '';
      // @ts-ignore
      errorMessages[currentField] = errorMessageMap[currentField] && errorMessageMap[currentField][firstErrorType] || '';
      return errorMessages;
    }

    for (const eachControlName in formControls) {
      const isControlValid = form.controls[eachControlName].valid;
      if (!isControlValid) {
        const errors = form.controls[eachControlName].errors;
        const firstErrorType = errors ? Object.keys(errors)[0] : null;
        if (firstErrorType) {
          form.controls[eachControlName].markAsTouched();
          // @ts-ignore
          errorMessages[eachControlName] = errorMessageMap[eachControlName][firstErrorType];
        }
      }
    }
    return errorMessages;
  }

  notification(message: string, type = '', actionText?: string, duration = 2000) {
    this.snackBar.open(message, actionText, {
      duration,
      verticalPosition: 'bottom', // 'top' | 'bottom'
      horizontalPosition: 'end', // 'start' | 'center' | 'end' | 'left' | 'right'
      panelClass: [type],
    });
  }

  openInNewTab(url: string) {
    window.open(url, '_blank');
  }

  openInCurrentTab(url: string) {
    window.open(url, '_self');
  }

  clearAppState() {
    this.closeDialog();

    this.urlParams.next(null);

    this.store.dispatch(ClearAdminGroupsListState());
    this.store.dispatch(ClearAuthenticationState());
    this.store.dispatch(ClearBehaviorTrackerState());
    this.store.dispatch(ClearClientDetailsState());
    this.store.dispatch(ClearClientsListState());
    this.store.dispatch(ClearCoachGroupsListState());
    this.store.dispatch(ClearGroupDashboardState());
    this.store.dispatch(ClearGroupDetailsState());
    this.store.dispatch(ClearInviteState());
    this.store.dispatch(ClearManageUsersState());
    this.store.dispatch(ClearMetricsState());
    this.store.dispatch(ClearNotificationsState());
    this.store.dispatch(ClearParticipantsListState());
    this.store.dispatch(ClearQuestionnairesState());
    this.store.dispatch(ClearResourceHubState());
    this.store.dispatch(ClearSessionsListState());
    this.store.dispatch(ClearSharedState());
    this.store.dispatch(ClearSelectedUserData());
    this.store.dispatch(ClearReportsState());
    this.store.dispatch(ClearProgressState());
    this.store.dispatch(ClearContractsState());

    this.clearStorage();
  }

  clearStorage() {
    this.removeAuthenticationToken();
    this.removeRegisterOtpToken();
    this.removeRegisterToken();
    this.removeSetPasswordToken();
    this.removeOnboardingToken();
    this.removeLoginOTPToken()

    const appLocalStorageKeys = [
      "ss-cord.admin-groups-list",
      "ss-cord.authentication",
      "ss-cord.authentication",
      "ss-cord.behavior-tracker",
      "ss-cord.client-details",
      "ss-cord.clients-list",
      "ss-cord.coach-groups-list",
      "ss-cord.group-dashboard",
      "ss-cord.group-details",
      "ss-cord.invite",
      "ss-cord.manage-users",
      "ss-cord.metrics",
      "ss-cord.notifications",
      "ss-cord.participants-list",
      "ss-cord.progress",
      "ss-cord.questionnaires",
      "ss-cord.resources-hub",
      "ss-cord.sessions-list",
      "ss-cord.shared",
      'ss-cord.reports',
      'ss-cord.contracts',
      'ss-cord.my-journal',
      'ss-cord.participant-groups-list'
    ];

    appLocalStorageKeys.forEach((key) => {
      this.removeFromSessionStorage(key);
    });
  }

  logout() {
    this.messageService.clear();
    this.store.dispatch(LogoutRequest());
  }

  openDialog(params: any) {
    return this.openDesktopDialog(params);
  }

  closeDialog() {
    // Close mobile dialog.
    this.closeMobileDialog.next();

    // Close desktop dialog
    this.closeDesktopDialog();
  }

  validatePassword(control: AbstractControl): ValidationErrors | null {
    const password = control.value;
    let errors = {};

    const containsSpace = new RegExp('\\s');

    if (containsSpace.test(password)) {
      errors = {
        ...errors,
        containsSpaceCheckFailed: true
      }
      return errors;
    }

    const containsLowercase = new RegExp('[a-z]');
    if (!containsLowercase.test(password)) {
      errors = {
        ...errors,
        lowerCaseCheckFailed: true
      }
      return errors;
    }

    const containsUppercase = new RegExp('[A-Z]');
    if (!containsUppercase.test(password)) {
      errors = {
        ...errors,
        upperCaseCheckFailed: true
      }
      return errors;
    }

    const containsNumber = new RegExp('[0-9]');
    if (!containsNumber.test(password)) {
      errors = {
        ...errors,
        numberCheckFailed: true
      }
      return errors;
    }

    const containsSpecialChar = new RegExp('[!@#$%^&*()\\\\/\\[\\]\-_=+{}|?>.<,:;~`\'÷×₹€¥"]');
    if (!containsSpecialChar.test(password)) {
      errors = {
        ...errors,
        specialCharCheckFailed: true
      }
      return errors;
    }

    return null;
  }

  validateUsername(control: AbstractControl): ValidationErrors | null {
    const username = control.value;
    let errors = {};

    const containsSpace = new RegExp('\\s');

    if (containsSpace.test(username)) {
      errors = {
        ...errors,
        spaceCheckFailed: true
      }
      return errors;
    }

    const containsSpecialChar = new RegExp('[!@#$%^&*()\\\\/\\[\\]\=+{}|?><,:;~`\'÷×₹€¥"]');
    if (containsSpecialChar.test(username)) {
      errors = {
        ...errors,
        specialCharCheckFailed: true
      }
      return errors;
    }

    const containsDigitsAtStart = new RegExp('^[0-9_.-]\\w*$');
    if (containsDigitsAtStart.test(username)) {
      errors = {
        ...errors,
        digitsAtStartCheckFailed: true
      }
      return errors;
    }

    return null;
  }

  setFocus(form: FormGroup = new FormGroup({}), errorMessages: any = {}, focus: any = {}) {
    const formFieldsList = Object.keys(form.controls);

    for (const eachFormField of formFieldsList) {
      if (errorMessages[eachFormField] !== undefined) {
        focus[eachFormField] = true;
        return;
      } else {
        focus[eachFormField] = false;
      }
    }
  }

  headerRemOffsetCalculator() {
    const currentScreenWidth = window.innerWidth;
    let remBase = 20;

    if (currentScreenWidth < 1439) {
      remBase = 8;
    } else if (currentScreenWidth < 1919) {
      remBase = 10;
    } else if (currentScreenWidth < 2559) {
      remBase = 12;
    } else if (currentScreenWidth < 3439) {
      remBase = 16;
    }

    return 14 * remBase;
  }

  openConfirmationDialog(config = {}) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'confirmation-dialog';
    return this.dialog.open(ConfirmationDialogComponent, {...dialogConfig, disableClose: true, ...config});
  }

  handleResourceAccessResponse(response, key, value) {
    if (response.success && response.data) {
      this.requestedResourceId.next([key, value]);

      this.store.select(drillDownUrls)
        .subscribe(data => {
          if (!data?.length) {
            this.store.dispatch(SetDrillDownUrls({payload: ['/']}));
          }
        }).unsubscribe();
    } else {
      this.store.select(drillDownUrls)
        .subscribe(data => {
          let updatedData = [...data];
          let prevUrl = updatedData.pop();

          if (!prevUrl) {
            prevUrl = '/';
            updatedData = ['/'];
          }
          (prevUrl)
          this.router.navigate([prevUrl]).then(() => {
            this.store.dispatch(SetDrillDownUrls({payload: updatedData}));
          });
        }).unsubscribe();

      let message = 'You do not have access to the requested resource.';

      if (!response.success) {
        message = response.message;
      }

      this.notification(message, 'danger');
    }
  }

  generateUsername(payload) {
    this.store.dispatch(GenerateUsernameRequest({payload}));
  }

  verifyUsername(payload) {
    this.store.dispatch(VerifyUsernameRequest({payload}));
  }

  /***
   * Update router query params.
   * @param params pass params to be updated, if params has null value then the param will be removed from URL.
   */
  updateQueryParams(params: Params) {
    this.router.navigate([], {
      queryParams: params,
      queryParamsHandling: 'merge'
    })
  }

  camelCase(text: string, alwaysStartWithLowerLetter = false) {
    const camelCase = text.replace(/[-_ ](.)/g, function (match, group1) {
      return group1.toUpperCase();
    });

    if (alwaysStartWithLowerLetter) {
      return camelCase.charAt(0).toLowerCase() + camelCase.slice(1);
    }
    return camelCase;
  }

  checkInfiniteScrollHeightAndCallNext(containerClassName: string, callback: () => void) {
    const mainContainer = document.getElementsByClassName(containerClassName)
      .item(0)

    if (mainContainer?.scrollHeight === mainContainer?.clientHeight) {
      callback();
    }
  }

  clearFormValidationOnlyIf(controls: string[], allowCheckFunction: () => boolean) {
    return (formGroup: FormGroup): ValidationErrors => {
      if (allowCheckFunction()) {
        controls.forEach((controlName) => {
          formGroup.controls[controlName].setErrors(null);
        });
      }
      return null;
    };
  }

  printElement(elementId: string) {
    const printClass = 'printing-element';
    const printContents = document.getElementById(elementId).innerHTML;
    const appRoot = document.getElementsByTagName('app-root').item(0);
    appRoot.classList.add(printClass);

    const newElement = document.createElement('div');
    newElement.style.height = '100vh';
    newElement.style.width = '100vw';

    newElement.innerHTML = printContents;

    document.body.append(newElement);

    window.print();

    newElement.remove();
    appRoot.classList.remove(printClass);
  }

  /**
   * Wait for given duration.
   * @param ms Milliseconds
   */
  wait(ms?: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    })
  }

  convertSecondsToMinutesAndSeconds(seconds) {
    // Calculate minutes and remaining seconds
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    // Format the result as MM:SS
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds)
      .padStart(2, '0')}`;
  }

  closeDrillDown() {
    this.store.dispatch(ClearSelectedUserData());
    this.store.select(drillDownUrls)
      .subscribe(data => {
        const updatedData = [...data];
        const prevUrl = updatedData.pop();
        const urlArr = prevUrl.split('?');
        const qp = {};
        if (urlArr[1]) {
          urlArr[1].split('&').forEach(o => {
            qp[o.split('=')[0]] = o.split('=')[1];
          })
        }

        this.router.navigate([urlArr[0]], {
          queryParams: qp
        }).then(() => {
          this.store.dispatch(SetDrillDownUrls({payload: updatedData}))
        });
      }).unsubscribe();
  }

  scrollColumnInView(columnId: string) {
    const element = document.getElementById(columnId)
    element?.scrollIntoView({behavior: "smooth", block: "end", inline: "nearest"});
  }

  convertMinutesToHrsAndMinutes(minutes: number) {
    return this.hoursAndMinutesPipe.transform(minutes);
  }

  createAndDownload(url, download = "download.txt", type = "text/plain") {
    const href = url;
    const anchorTagElement = document.createElement("a");
    anchorTagElement.href = href;
    anchorTagElement.download = download;
    document.getElementsByTagName("body").item(0).append(anchorTagElement);
    anchorTagElement.click();
    anchorTagElement.remove();
    URL.revokeObjectURL(href);
  }

  getDirtyValues(form: any) {
    let dirtyValues: any = {};

    Object.keys(form.controls)
      .forEach(key => {
        const currentControl = form.controls[key];

        if (currentControl.dirty) {
          if (currentControl.controls)
            dirtyValues[key] = this.getDirtyValues(currentControl);
          else
            dirtyValues[key] = currentControl.value;
        }
      });

    return dirtyValues;
  }

  hasDirtyAndInvalidValues(name, form: any, list = []) {
    if (Array.isArray(form.controls)) {
      for (const control of form.controls) {
        this.hasDirtyAndInvalidValues(null, control, list);
      }
    } else if (form.controls) {
      for (const field in form.controls) {
        this.hasDirtyAndInvalidValues(field, form.controls[field], list);
      }
    } else if (form.invalid && form.dirty && name) {
      list.push(name);
    }
    return list;
  }

  calculateXForPopup(node: any, x: number) {
    x += 15;
    const nodeWidth: number = node.node().offsetWidth;
    if ((x + nodeWidth) > document.body.offsetWidth) {
      x = document.body.offsetWidth - nodeWidth - 10;
    }
    return x;
  }

  convertRemToPx(rem) {
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  oldNewDateFormatter(date) {
    return moment(date).startOf('day').isBefore(moment().startOf('day')) ?
           moment(date).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z' :
           moment.utc(date).format()
  }
}
